import React from 'react';
import { motion } from 'motion/react';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Activity, 
  Lock, 
  Globe, 
  Server, 
  Database,
  ArrowUpRight,
  ArrowDownRight,
  Zap
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';

const threatData = [
  { time: '00:00', threats: 12, blocked: 12 },
  { time: '04:00', threats: 18, blocked: 18 },
  { time: '08:00', threats: 45, blocked: 44 },
  { time: '12:00', threats: 32, blocked: 32 },
  { time: '16:00', threats: 28, blocked: 28 },
  { time: '20:00', threats: 15, blocked: 15 },
  { time: '23:59', threats: 10, blocked: 10 },
];

const riskScoreData = [
  { name: 'Secure', value: 85 },
  { name: 'Risk', value: 15 },
];

const COLORS = ['#0096FF', 'rgba(255, 255, 255, 0.05)'];

const vulnerabilities = [
  { id: 'VULN-2026-041', system: 'POS Terminal #42', severity: 'CRITICAL', status: 'PATCHING', impact: 'High' },
  { id: 'VULN-2026-038', system: 'Cloud API Gateway', severity: 'HIGH', status: 'MONITORING', impact: 'Medium' },
  { id: 'VULN-2026-035', system: 'Customer DB (AWS)', severity: 'MEDIUM', status: 'RESOLVED', impact: 'High' },
];

export default function DashboardPreview() {
  return (
    <section className="py-40 relative overflow-hidden bg-secondary/5">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-20 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
            Intelligence Interface
          </div>
          <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight leading-tight">
            The <span className="text-accent">Agrand</span> Dashboard
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-normal max-w-2xl mx-auto leading-relaxed">
            Real-time visibility into your retail cloud infrastructure. Professional intelligence reports delivered with precision.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Dashboard View */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8 glass-card rounded-3xl p-8 md:p-10 border-white/5 bg-black/40 backdrop-blur-2xl relative overflow-hidden"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
              <div className="space-y-1">
                <h3 className="text-xl font-display font-bold tracking-tight flex items-center gap-3">
                  <Activity className="w-5 h-5 text-accent" /> Live Threat Monitoring
                </h3>
                <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">System Status: Operational // 0.00ms Latency</p>
              </div>
              <div className="flex gap-4">
                <div className="px-3 py-1.5 rounded-lg bg-accent/5 border border-accent/10 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  <span className="text-[9px] font-bold text-accent uppercase tracking-widest">Active Scan</span>
                </div>
              </div>
            </div>

            <div className="min-h-[350px] w-full relative">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={350}>
                <AreaChart data={threatData}>
                  <defs>
                    <linearGradient id="colorThreat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0096FF" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#0096FF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis 
                    dataKey="time" 
                    stroke="rgba(255,255,255,0.2)" 
                    fontSize={10} 
                    tickLine={false} 
                    axisLine={false}
                    fontFamily="JetBrains Mono"
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.2)" 
                    fontSize={10} 
                    tickLine={false} 
                    axisLine={false}
                    fontFamily="JetBrains Mono"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.9)', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontFamily: 'JetBrains Mono'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="threats" 
                    stroke="#0096FF" 
                    fillOpacity={1} 
                    fill="url(#colorThreat)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
              {[
                { label: "Total Requests", value: "1.2M", change: "+12%", up: true },
                { label: "Threats Blocked", value: "4,281", change: "-5%", up: false },
                { label: "Auth Failures", value: "128", change: "+2%", up: true },
                { label: "Avg Response", value: "14ms", change: "-2ms", up: false },
              ].map((stat, i) => (
                <div key={i} className="p-5 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{stat.label}</p>
                  <div className="flex items-end justify-between">
                    <span className="text-xl font-display font-bold">{stat.value}</span>
                    <span className={`text-[9px] font-bold flex items-center ${stat.up ? 'text-red-500' : 'text-green-500'}`}>
                      {stat.up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.change}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Side Panels */}
          <div className="lg:col-span-4 space-y-8">
            {/* Risk Score Card */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-3xl p-8 border-white/5 bg-black/40 backdrop-blur-2xl relative overflow-hidden"
            >
              <div className="space-y-6 text-center">
                <h3 className="text-lg font-display font-bold tracking-tight">Enterprise Risk Score</h3>
                <div className="relative min-h-[160px] w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                    <PieChart>
                      <Pie
                        data={riskScoreData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={75}
                        paddingAngle={5}
                        dataKey="value"
                        startAngle={180}
                        endAngle={0}
                      >
                        {riskScoreData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
                    <span className="text-4xl font-display font-black text-accent">85</span>
                    <span className="text-[8px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Resilience Rating</span>
                  </div>
                </div>
                <div className="p-2.5 rounded-xl bg-green-500/5 border border-green-500/10">
                  <p className="text-green-500 text-[9px] font-bold flex items-center justify-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5" /> Security Posture: Optimal
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Vulnerability Report Preview */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-3xl p-8 border-white/5 bg-black/40 backdrop-blur-2xl"
            >
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-display font-bold tracking-tight">Active Intelligence</h3>
                  <Zap className="w-4 h-4 text-accent" />
                </div>
                <div className="space-y-3">
                  {vulnerabilities.map((vuln, i) => (
                    <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5 space-y-2 group hover:bg-white/[0.04] transition-all cursor-default">
                      <div className="flex justify-between items-center">
                        <span className="text-[8px] font-mono text-accent">{vuln.id}</span>
                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${
                          vuln.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-500' : 
                          vuln.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-500' : 
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {vuln.severity}
                        </span>
                      </div>
                      <p className="text-xs font-bold tracking-tight">{vuln.system}</p>
                      <div className="flex justify-between items-center text-[8px] text-muted-foreground uppercase tracking-widest font-bold">
                        <span>Status: {vuln.status}</span>
                        <span>Impact: {vuln.impact}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="w-full py-3 rounded-xl border border-white/10 text-[9px] font-bold uppercase tracking-[0.2em] hover:bg-white/5 transition-colors">
                  Download Full Report (PDF)
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Callout */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center space-y-6"
        >
          <p className="text-muted-foreground font-normal max-w-xl mx-auto italic text-sm">
            "The Agrand dashboard gave our board the clarity they needed to approve our cloud-native migration with full confidence."
          </p>
          <div className="flex items-center justify-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold text-[10px]">JD</div>
            <div className="text-left">
              <p className="text-[11px] font-bold">Jameson D.</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">CTO, Global Retail Group</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
